import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ArrowOutwardRounded,
  ChatBubbleOutlineRounded,
  PushPinRounded,
  RefreshRounded,
  ReplyRounded,
  SendRounded,
  SortRounded,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { backend_api } from "../../../static/server";
import { appendTeamMemberLawyerId } from "../../../styles/styles";
import { emitCommentNotificationsRefresh } from "./useCommentNotifications";

/* ─────────────────────────────────────────
   Constants & helpers
───────────────────────────────────────── */

const moduleOptions = [
  { value: "task", label: "Task" },
  { value: "matter", label: "Matter" },
  { value: "people", label: "People" },
  { value: "company", label: "Company" },
  { value: "activity", label: "Activity" },
  { value: "bill", label: "Bill" },
  { value: "bank_account", label: "Bankers" },
  { value: "bank_transaction", label: "Bank transaction" },
  { value: "document", label: "Document" },
];

const buildCommentRequestUrl = (basePath, teamMemberLawyerId) =>
  appendTeamMemberLawyerId(`${backend_api}${basePath}`, teamMemberLawyerId);

const slugifyRouteLabel = (value) =>
  String(value || "record")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const getCommentModuleKey = (comment) => {
  if (!comment) return "task";
  if (comment.entity_type === "contact") {
    return comment.entity_meta?.contact_type === "company"
      ? "company"
      : "people";
  }
  return comment.entity_type;
};

const getModuleLabel = (moduleKey) =>
  moduleOptions.find((o) => o.value === moduleKey)?.label || "Record";

const getPrimaryRouteForComment = (comment) => {
  if (!comment) return "/dashboard";
  const moduleKey = getCommentModuleKey(comment);
  if (comment.entity_type === "matter")
    return `/dashboard/matter/${comment.entity_id}`;
  if (moduleKey === "company")
    return `/dashboard/companies/${comment.entity_id}`;
  if (moduleKey === "people") return `/dashboard/people/${comment.entity_id}`;
  if (moduleKey === "bill") return "/dashboard/bills";
  if (moduleKey === "activity") return "/dashboard/activities";
  if (moduleKey === "bank_account") {
    const label = comment.entity_meta?.account_name || comment.entity_label;
    return `/dashboard/bank_accounts/${comment.entity_id}/${slugifyRouteLabel(label)}`;
  }
  if (moduleKey === "bank_transaction") {
    const { account_id: accountId, account_name: accountName } =
      comment.entity_meta || {};
    if (accountId)
      return `/dashboard/bank_accounts/${accountId}/${slugifyRouteLabel(accountName)}`;
    return "/dashboard/bank_accounts";
  }
  if (moduleKey === "document") return "/dashboard/documents";
  return "/dashboard/tasks";
};

const getEntityTone = (moduleKey) => {
  switch (moduleKey) {
    case "matter":
      return { bg: "rgba(37,99,235,0.12)", color: "#2563EB" };
    case "people":
    case "company":
      return { bg: "rgba(15,157,88,0.14)", color: "#0F9D58" };
    case "bill":
      return { bg: "rgba(245,158,11,0.14)", color: "#D97706" };
    case "activity":
      return { bg: "rgba(14,165,233,0.14)", color: "#0284C7" };
    case "bank_account":
    case "bank_transaction":
      return { bg: "rgba(16,185,129,0.14)", color: "#059669" };
    case "document":
      return { bg: "rgba(236,72,153,0.14)", color: "#DB2777" };
    default:
      return { bg: "rgba(124,58,237,0.14)", color: "#7C3AED" };
  }
};

const formatRelativeCommentTime = (value) => {
  if (!value) return "Just now";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Just now";
  const diffMins = Math.round((Date.now() - date.getTime()) / 60000);
  if (diffMins <= 1) return "Just now";
  if (diffMins < 60) return `${diffMins} minutes ago`;
  const diffHours = Math.round(diffMins / 60);
  if (diffHours < 24) return `${diffHours} hours ago`;
  const diffDays = Math.round(diffHours / 24);
  if (diffDays < 7) return `${diffDays} days ago`;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const getInitials = (name) => {
  if (!name) return "C";
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() || "")
    .join("");
};

/* ─────────────────────────────────────────
   Empty state
───────────────────────────────────────── */
const EmptyCommentsState = ({
  title = "No comments yet",
  description = "When the team posts updates on tasks, matters, contacts, bills, bank records, or documents, they'll appear here.",
}) => (
  <Box
    sx={{
      py: 6,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 1.5,
      textAlign: "center",
    }}
  >
    <ChatBubbleOutlineRounded sx={{ fontSize: 36, color: "text.disabled" }} />
    <Typography
      variant="body2"
      sx={{ fontWeight: 600, color: "text.secondary" }}
    >
      {title}
    </Typography>
    <Typography
      variant="caption"
      sx={{ maxWidth: 320, color: "text.disabled" }}
    >
      {description}
    </Typography>
  </Box>
);

const CommentFeedSkeleton = ({ showFilters = true }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 1,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Skeleton variant="text" width={110} height={28} />
        <Skeleton variant="rounded" width={30} height={22} sx={{ borderRadius: "999px" }} />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Skeleton variant="rounded" width={140} height={38} sx={{ borderRadius: "999px" }} />
        <Skeleton variant="circular" width={28} height={28} />
      </Box>
    </Box>

    {showFilters ? (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
        {[96, 84, 90, 78].map((width, index) => (
          <Skeleton
            key={index}
            variant="rounded"
            width={width}
            height={24}
            sx={{ borderRadius: "999px" }}
          />
        ))}
      </Box>
    ) : null}

    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.2 }}>
      {[0, 1, 2].map((item) => (
        <Box key={item} sx={{ display: "flex", gap: `${AVATAR_GAP}px` }}>
          <Skeleton variant="circular" width={36} height={36} />
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.8, flexWrap: "wrap" }}>
              <Skeleton variant="text" width={108} height={22} />
              <Skeleton variant="text" width={72} height={18} />
              <Skeleton variant="rounded" width={72} height={20} sx={{ borderRadius: "999px" }} />
            </Box>
            <Skeleton variant="text" width="90%" height={22} />
            <Skeleton variant="text" width="72%" height={22} />
            <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
              <Skeleton variant="rounded" width={68} height={24} sx={{ borderRadius: "999px" }} />
              <Skeleton variant="rounded" width={24} height={24} sx={{ borderRadius: "999px" }} />
            </Box>

            {item === 0 ? (
              <Box sx={{ ml: `${AVATAR_SIZE + AVATAR_GAP}px`, mt: 1.6, display: "flex", gap: `${AVATAR_GAP}px` }}>
                <Skeleton variant="circular" width={30} height={30} />
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.6 }}>
                    <Skeleton variant="text" width={92} height={20} />
                    <Skeleton variant="text" width={64} height={18} />
                  </Box>
                  <Skeleton variant="text" width="82%" height={20} />
                  <Skeleton variant="text" width="66%" height={20} />
                </Box>
              </Box>
            ) : null}
          </Box>
        </Box>
      ))}
    </Box>
  </Box>
);

/* ─────────────────────────────────────────
   CommentRow  — one root or one reply
───────────────────────────────────────── */
const AVATAR_SIZE = 36; // root
const AVATAR_SIZE_R = 30; // reply
const AVATAR_GAP = 12; // gap between avatar column and content

const CommentRow = ({
  comment,
  isReply = false,
  isHighlighted = false,
  drawThreadBelow = false, // extends vertical line from root avatar downward
  replyOpen,
  replyValue,
  submitting,
  onNavigate,
  onPin,
  onReply,
  onReplyChange,
  onReplySubmit,
}) => {
  const moduleKey = getCommentModuleKey(comment);
  const tone = getEntityTone(moduleKey);
  const avatarSize = isReply ? AVATAR_SIZE_R : AVATAR_SIZE;

  return (
    <Box sx={{ display: "flex", gap: `${AVATAR_GAP}px` }}>
      {/* ── Avatar + optional thread line ── */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          flexShrink: 0,
          width: avatarSize,
        }}
      >
        <Avatar
          sx={{
            width: avatarSize,
            height: avatarSize,
            bgcolor: tone.bg,
            color: tone.color,
            fontSize: isReply ? "0.62rem" : "0.72rem",
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {getInitials(comment.created_by?.name)}
        </Avatar>

        {/* Vertical thread connector drawn below the avatar */}
        {drawThreadBelow && (
          <Box
            sx={{
              flex: 1,
              width: "2px",
              bgcolor: "rgba(0,0,0,0.09)",
              mt: "5px",
              borderRadius: "999px",
            }}
          />
        )}
      </Box>

      {/* ── Content ── */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          pb: "4px",
          px: isHighlighted ? "10px" : 0,
          py: isHighlighted ? "8px" : 0,
          borderRadius: "14px",
          border: isHighlighted
            ? "1px solid rgba(59,130,246,0.22)"
            : "1px solid transparent",
          bgcolor: isHighlighted ? "rgba(59,130,246,0.08)" : "transparent",
          boxShadow: isHighlighted
            ? "0 10px 24px rgba(59,130,246,0.08)"
            : "none",
        }}
      >
        {/* Author row */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "6px",
            mb: "4px",
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "0.82rem",
              lineHeight: 1.3,
              color: "text.primary",
            }}
          >
            {comment.created_by?.name || "Team member"}
          </Typography>

          <Typography
            sx={{
              fontSize: "0.74rem",
              color: "text.secondary",
              lineHeight: 1.3,
            }}
          >
            {formatRelativeCommentTime(comment.created_at)}
          </Typography>

          {/* Module badge */}
          <Box
            sx={{
              bgcolor: tone.bg,
              color: tone.color,
              borderRadius: "999px",
              px: "7px",
              py: "1px",
              fontSize: "0.65rem",
              fontWeight: 700,
              lineHeight: "18px",
            }}
          >
            {getModuleLabel(moduleKey)}
          </Box>

          {/* Entity label */}
          <Typography
            sx={{
              fontSize: "0.7rem",
              color: "text.disabled",
              maxWidth: 180,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {comment.entity_label || `#${comment.entity_id}`}
          </Typography>
        </Box>

        {/* Body */}
        <Typography
          sx={{
            fontSize: "0.84rem",
            lineHeight: 1.65,
            color: "text.primary",
            whiteSpace: "pre-wrap",
          }}
        >
          {comment.content}
        </Typography>

        {/* Actions */}
        <Box
          sx={{
            mt: "8px",
            display: "flex",
            alignItems: "center",
            gap: "2px",
            flexWrap: "wrap",
          }}
        >
          {/* Reply */}
          <Button
            size="small"
            startIcon={<ReplyRounded sx={{ fontSize: "0.88rem !important" }} />}
            onClick={() => onReply(comment)}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              fontSize: "0.76rem",
              color: "text.secondary",
              px: "8px",
              py: "3px",
              minWidth: 0,
              borderRadius: "999px",
              "&:hover": { bgcolor: "rgba(0,0,0,0.05)", color: "text.primary" },
            }}
          >
            Reply
          </Button>

          {/* Pin */}
          <Tooltip title={comment.is_pinned ? "Unpin" : "Pin"}>
            <IconButton
              size="small"
              sx={{ p: "4px" }}
              onClick={() => onPin(comment)}
            >
              <PushPinRounded
                sx={{
                  fontSize: "0.92rem",
                  color: comment.is_pinned
                    ? "#D97706"
                    : "rgba(100,116,139,0.55)",
                }}
              />
            </IconButton>
          </Tooltip>

          {/* Go to record */}
          <Tooltip title="Open record">
            <IconButton
              size="small"
              sx={{ p: "4px" }}
              onClick={() => onNavigate(comment)}
            >
              <ArrowOutwardRounded
                sx={{ fontSize: "0.92rem", color: "rgba(100,116,139,0.55)" }}
              />
            </IconButton>
          </Tooltip>

          {comment.reply_count > 0 && (
            <Chip
              size="small"
              label={`${comment.reply_count} repl${comment.reply_count === 1 ? "y" : "ies"}`}
              variant="outlined"
              sx={{ height: 20, fontSize: "0.67rem" }}
            />
          )}
          {comment.is_pinned && (
            <Chip
              size="small"
              label="Pinned"
              color="warning"
              sx={{ height: 20, fontSize: "0.67rem" }}
            />
          )}
          {comment.entry_type === "note" && (
            <Chip
              size="small"
              label="Private"
              variant="outlined"
              sx={{ height: 20, fontSize: "0.67rem" }}
            />
          )}
        </Box>

        {/* Inline reply composer */}
        {replyOpen && (
          <Box
            sx={{
              mt: "12px",
              display: "flex",
              flexDirection: "column",
              gap: "8px",
            }}
          >
            <TextField
              value={replyValue}
              onChange={(e) => onReplyChange(e.target.value)}
              multiline
              minRows={2}
              placeholder="Write a reply…"
              fullWidth
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  fontSize: "0.83rem",
                  bgcolor: "rgba(0,0,0,0.025)",
                  "& fieldset": { borderColor: "rgba(0,0,0,0.12)" },
                },
              }}
            />
            <Box
              sx={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}
            >
              <Button
                size="small"
                onClick={() => onReply(null)}
                sx={{
                  textTransform: "none",
                  fontSize: "0.78rem",
                  color: "text.secondary",
                  borderRadius: "999px",
                }}
              >
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                endIcon={
                  submitting ? (
                    <CircularProgress size={12} color="inherit" />
                  ) : (
                    <SendRounded sx={{ fontSize: "0.82rem !important" }} />
                  )
                }
                onClick={() => onReplySubmit(comment)}
                disabled={submitting}
                sx={{
                  textTransform: "none",
                  fontSize: "0.78rem",
                  borderRadius: "999px",
                  fontWeight: 700,
                  px: "14px",
                }}
              >
                {submitting ? "Posting…" : "Reply"}
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

/* ─────────────────────────────────────────
   CommentBlock  =  root + indented replies
───────────────────────────────────────── */
const CommentBranch = ({
  node,
  childMap,
  depth = 0,
  highlightCommentId,
  replyTargetId,
  replyDraft,
  replySubmittingId,
  onNavigate,
  onPin,
  onReply,
  onReplyChange,
  onReplySubmit,
}) => {
  const children = childMap.get(node.id) || [];
  const hasReplies = children.length > 0;
  const currentReplyOpen = replyTargetId === node.id;
  const drawThread = hasReplies || currentReplyOpen;
  const avatarIndent = depth > 0 ? `${(AVATAR_SIZE + AVATAR_GAP) * depth}px` : 0;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        ml: avatarIndent,
        position: "relative",
      }}
    >
      <CommentRow
        comment={node}
        isReply={depth > 0}
        isHighlighted={node.id === highlightCommentId}
        drawThreadBelow={drawThread}
        replyOpen={currentReplyOpen}
        replyValue={currentReplyOpen ? replyDraft : ""}
        submitting={replySubmittingId === node.id}
        onNavigate={onNavigate}
        onPin={onPin}
        onReply={onReply}
        onReplyChange={onReplyChange}
        onReplySubmit={onReplySubmit}
      />

      {hasReplies && (
        <Box
          sx={{
            ml: `${depth > 0 ? AVATAR_SIZE_R + AVATAR_GAP : AVATAR_SIZE + AVATAR_GAP}px`,
            display: "flex",
            flexDirection: "column",
            gap: "14px",
            mt: "6px",
            position: "relative",
            "&::before": {
              content: '""',
              position: "absolute",
              left: depth > 0 ? "13px" : "16px",
              top: 0,
              bottom: 8,
              width: "2px",
              bgcolor: "rgba(15,23,42,0.08)",
              borderRadius: "999px",
              clipPath:
                "polygon(0 0, 100% 0, 100% calc(100% - 8px), 50% 100%, 0 calc(100% - 8px))",
            },
          }}
        >
          {children.map((child) => (
            <CommentBranch
              key={child.id}
              node={child}
              childMap={childMap}
              depth={depth + 1}
              highlightCommentId={highlightCommentId}
              replyTargetId={replyTargetId}
              replyDraft={replyDraft}
              replySubmittingId={replySubmittingId}
              onNavigate={onNavigate}
              onPin={onPin}
              onReply={onReply}
              onReplyChange={onReplyChange}
              onReplySubmit={onReplySubmit}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

/* ─────────────────────────────────────────
   Main panel
───────────────────────────────────────── */
const CommentFeedPanel = ({
  token,
  teamMemberLawyerId,
  entityOptions = [],
  variant = "dashboard",
  maxItems,
  onCountChange,
  refreshSignal,
  feedType = "global",
  entityType,
  entityId,
  entryTypeFilter = "all",
  title,
  hideModuleFilters = false,
  emptyStateTitle,
  emptyStateDescription,
  threadIdsFilter = null,
  highlightCommentId = null,
}) => {
  const navigate = useNavigate();
  const isCompact = variant === "compact";
  const isCommunication = variant === "communication";

  const [comments, setComments] = useState([]);
  const [commentCount, setCommentCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshTick, setRefreshTick] = useState(0);
  const [replyTargetId, setReplyTargetId] = useState(null);
  const [replyDraft, setReplyDraft] = useState("");
  const [replySubmittingId, setReplySubmittingId] = useState(null);
  const [activeModuleFilter, setActiveModuleFilter] = useState("all");
  const [sortMode, setSortMode] = useState("recent");
  const resolvedTitle =
    title || (entryTypeFilter === "note" ? "Notes" : "Comments");
  const shouldHideModuleFilters =
    hideModuleFilters || feedType === "entity" || entryTypeFilter === "note";

  /* fetch */
  const fetchComments = useCallback(async () => {
    if (!token) return;
    if (feedType === "entity" && (!entityType || !entityId)) return;
    setLoading(true);
    setError("");
    try {
      const basePageSize = maxItems || (isCompact ? 8 : isCommunication ? 30 : 24);
      const threadFilterBoost = Array.isArray(threadIdsFilter)
        ? Math.max(threadIdsFilter.length * 6, basePageSize)
        : basePageSize;
      const pageSize =
        entryTypeFilter === "all" && !Array.isArray(threadIdsFilter)
          ? basePageSize
          : Math.min(Math.max(basePageSize * 3, threadFilterBoost), 100);
      const query = new URLSearchParams({
        page_size: String(pageSize),
      });
      if (entryTypeFilter !== "all") {
        query.set("entry_type", entryTypeFilter);
      }
      if (feedType === "entity") {
        query.set("entity_type", entityType);
        query.set("entity_id", String(entityId));
      }

      const basePath =
        feedType === "entity"
          ? `comments/?${query.toString()}`
          : `comments/global/?${query.toString()}`;
      const url = buildCommentRequestUrl(basePath, teamMemberLawyerId);
      const { data } = await axios.get(url, {
        headers: { Authorization: `Token ${token}` },
      });
      const results = Array.isArray(data) ? data : data?.results || [];
      setComments(results);
      setCommentCount(Array.isArray(data) ? results.length : data?.count || 0);
    } catch {
      setError(
        entryTypeFilter === "note"
          ? "Unable to load notes."
          : "Unable to load comments.",
      );
    } finally {
      setLoading(false);
    }
  }, [
    entityId,
    entityType,
    entryTypeFilter,
    feedType,
    isCommunication,
    isCompact,
    maxItems,
    threadIdsFilter,
    teamMemberLawyerId,
    token,
  ]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments, refreshSignal, refreshTick]);

  const handleRefresh = () => setRefreshTick((c) => c + 1);

  const navigateToCommentEntity = (comment) => {
    navigate(getPrimaryRouteForComment(comment), {
      state: {
        commentContext: {
          commentId: comment.id,
          entityType: comment.entity_type,
          entityId: comment.entity_id,
        },
      },
    });
  };

  const handleSubmitReply = async (comment) => {
    const content = String(replyDraft || "").trim();
    if (!content) {
      toast.error("Reply cannot be empty.");
      return;
    }
    try {
      setReplySubmittingId(comment.id);
      const url = buildCommentRequestUrl("comments/", teamMemberLawyerId);
      await axios.post(
        url,
        {
          content,
          entry_type: comment.entry_type || "comment",
          entity_type: comment.entity_type,
          entity_id: comment.entity_id,
          parent_comment_id: comment.id,
        },
        { headers: { Authorization: `Token ${token}` } },
      );
      toast.success("Reply posted.");
      setReplyDraft("");
      setReplyTargetId(null);
      if ((comment.entry_type || "comment") === "comment") {
        emitCommentNotificationsRefresh();
      }
      handleRefresh();
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.content?.[0] ||
        err?.response?.data?.parent_comment_id?.[0] ||
        "Failed to post reply.";
      toast.error(String(msg));
    } finally {
      setReplySubmittingId(null);
    }
  };

  const handleTogglePin = async (comment) => {
    try {
      const url = buildCommentRequestUrl(
        `comments/${comment.id}/pin/`,
        teamMemberLawyerId,
      );
      await axios.patch(
        url,
        { is_pinned: !comment.is_pinned },
        { headers: { Authorization: `Token ${token}` } },
      );
      toast.success(comment.is_pinned ? "Unpinned." : "Pinned.");
      handleRefresh();
    } catch {
      toast.error("Unable to update pin.");
    }
  };

  /* derive sorted → filtered → threaded blocks */
  const sortedComments = useMemo(() => {
    const items = [...comments];
    items.sort((a, b) => {
      if (sortMode === "pinned" && a.is_pinned !== b.is_pinned)
        return a.is_pinned ? -1 : 1;
      return new Date(b.created_at) - new Date(a.created_at);
    });
    return items;
  }, [comments, sortMode]);

  const filteredComments = useMemo(() => {
    const typeFiltered =
      entryTypeFilter === "all"
        ? sortedComments
        : sortedComments.filter((c) => c.entry_type === entryTypeFilter);

    if (activeModuleFilter === "all" || shouldHideModuleFilters) {
      return typeFiltered;
    }

    return typeFiltered.filter(
      (c) => getCommentModuleKey(c) === activeModuleFilter,
    );
  }, [activeModuleFilter, entryTypeFilter, shouldHideModuleFilters, sortedComments]);

  useEffect(() => {
    const visibleCount =
      entryTypeFilter === "all" && activeModuleFilter === "all"
        ? commentCount
        : filteredComments.length;
    onCountChange?.(visibleCount);
  }, [
    activeModuleFilter,
    commentCount,
    entryTypeFilter,
    filteredComments.length,
    onCountChange,
  ]);

  const commentThreads = useMemo(() => {
    const commentMap = new Map(filteredComments.map((comment) => [comment.id, comment]));
    const childMap = new Map();

    filteredComments.forEach((comment) => {
      if (!comment.parent_comment_id) return;
      const siblings = childMap.get(comment.parent_comment_id) || [];
      siblings.push(comment);
      childMap.set(comment.parent_comment_id, siblings);
    });

    childMap.forEach((siblings, parentId) => {
      siblings.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      childMap.set(parentId, siblings);
    });

    const roots = filteredComments.filter(
      (comment) =>
        !comment.parent_comment_id || !commentMap.has(comment.parent_comment_id),
    );

    const filteredRoots =
      Array.isArray(threadIdsFilter) && threadIdsFilter.length > 0
        ? roots.filter((root) => threadIdsFilter.includes(root.id))
        : Array.isArray(threadIdsFilter)
          ? []
          : roots;

    const finalRoots = isCompact ? filteredRoots.slice(0, 6) : filteredRoots;
    return { roots: finalRoots, childMap };
  }, [filteredComments, isCompact, threadIdsFilter]);

  const filterChips = useMemo(() => {
    const keys = new Set([
      ...entityOptions.map((o) => o.moduleKey),
      ...comments.map((c) => getCommentModuleKey(c)),
    ]);
    return moduleOptions.filter((o) => keys.has(o.value));
  }, [comments, entityOptions]);

  const pinnedCount = useMemo(
    () => filteredComments.filter((c) => c.is_pinned).length,
    [filteredComments],
  );
  const replyCount = useMemo(
    () => filteredComments.filter((c) => c.parent_comment_id).length,
    [filteredComments],
  );

  /* ── Loading ── */
  if (loading) {
    return <CommentFeedSkeleton showFilters={!isCompact && !shouldHideModuleFilters} />;
  }

  /* ── Error ── */
  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <Button onClick={handleRefresh} size="small">
            Retry
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  /* ── Empty ── */
  if (commentThreads.roots.length === 0)
    return (
      <EmptyCommentsState
        title={
          emptyStateTitle ||
          (entryTypeFilter === "note" ? "No notes yet" : "No comments yet")
        }
        description={
          emptyStateDescription ||
          (entryTypeFilter === "note"
            ? "Private notes you save on tasks, matters, contacts, bills, bank records, and documents will appear here."
            : "When the team posts updates on tasks, matters, contacts, bills, bank records, or documents, they'll appear here.")
        }
      />
    );

  /* ── Feed ── */
  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: "20px",
          flexWrap: "wrap",
          gap: "10px",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Typography
            sx={{ fontWeight: 800, fontSize: "1rem", color: "text.primary" }}
          >
            {resolvedTitle}
          </Typography>
          <Box
            sx={{
              bgcolor: "#F97316",
              color: "#fff",
              borderRadius: "12px",
              px: "9px",
              lineHeight: "20px",
              fontSize: "0.72rem",
              fontWeight: 800,
            }}
          >
            {entryTypeFilter === "all" && activeModuleFilter === "all"
              ? commentCount
              : filteredComments.length}
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {!isCompact && (
            <TextField
              select
              size="small"
              value={sortMode}
              onChange={(e) => setSortMode(e.target.value)}
              sx={{
                minWidth: 138,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "999px",
                  fontSize: "0.78rem",
                  "& fieldset": { borderColor: "rgba(0,0,0,0.15)" },
                },
              }}
              InputProps={{
                startAdornment: (
                  <SortRounded
                    sx={{
                      fontSize: "0.92rem",
                      color: "text.secondary",
                      mr: "5px",
                    }}
                  />
                ),
              }}
            >
              <MenuItem value="recent" sx={{ fontSize: "0.8rem" }}>
                Most recent
              </MenuItem>
              <MenuItem value="pinned" sx={{ fontSize: "0.8rem" }}>
                Pinned first
              </MenuItem>
            </TextField>
          )}
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={handleRefresh} sx={{ p: "5px" }}>
              <RefreshRounded sx={{ fontSize: "0.98rem" }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Stats */}
      {!isCompact && !shouldHideModuleFilters && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px", mb: "12px" }}>
          <Chip
            size="small"
            label={`${pinnedCount} pinned`}
            variant="outlined"
            sx={{ fontSize: "0.69rem", height: 22 }}
          />
          <Chip
            size="small"
            label={`${replyCount} replies`}
            variant="outlined"
            sx={{ fontSize: "0.69rem", height: 22 }}
          />
        </Box>
      )}

      {/* Module filter */}
      {!isCompact && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px", mb: "24px" }}>
          <Chip
            size="small"
            label="All"
            clickable
            color={activeModuleFilter === "all" ? "primary" : "default"}
            variant={activeModuleFilter === "all" ? "filled" : "outlined"}
            onClick={() => setActiveModuleFilter("all")}
            sx={{ fontSize: "0.7rem", height: 24 }}
          />
          {filterChips.map((opt) => (
            <Chip
              key={opt.value}
              size="small"
              label={opt.label}
              clickable
              color={activeModuleFilter === opt.value ? "primary" : "default"}
              variant={activeModuleFilter === opt.value ? "filled" : "outlined"}
              onClick={() => setActiveModuleFilter(opt.value)}
              sx={{ fontSize: "0.7rem", height: 24 }}
            />
          ))}
        </Box>
      )}

      {/* Comment blocks */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: "22px" }}>
        {commentThreads.roots.map((root) => (
          <CommentBranch
            key={root.id}
            node={root}
            childMap={commentThreads.childMap}
            highlightCommentId={highlightCommentId}
            replyTargetId={replyTargetId}
            replyDraft={replyDraft}
            replySubmittingId={replySubmittingId}
            onNavigate={navigateToCommentEntity}
            onPin={handleTogglePin}
            onReply={(c) => {
              if (!c) {
                setReplyTargetId(null);
                return;
              }
              setReplyTargetId((cur) => (cur === c.id ? null : c.id));
              setReplyDraft("");
            }}
            onReplyChange={setReplyDraft}
            onReplySubmit={handleSubmitReply}
          />
        ))}
      </Box>
    </Box>
  );
};

/* ─────────────────────────────────────────
   Entity options builder
───────────────────────────────────────── */
export const buildDashboardCommentEntityOptions = ({
  tasks = [],
  matters = [],
  people = [],
  companies = [],
  bills = [],
  activities = [],
  bankAccounts = [],
  bankTransactions = [],
  documents = [],
}) => [
  ...matters
    .filter((m) => m?.id)
    .map((m) => ({
      id: m.id,
      entityType: "matter",
      moduleKey: "matter",
      moduleLabel: "Matter",
      label: m.case_name || m.name || `Matter #${m.id}`,
    })),
  ...tasks
    .filter((t) => t?.id)
    .map((t) => ({
      id: t.id,
      entityType: "task",
      moduleKey: "task",
      moduleLabel: "Task",
      label: t.task_name || `Task #${t.id}`,
    })),
  ...people
    .filter((c) => c?.id)
    .map((c) => ({
      id: c.id,
      entityType: "contact",
      moduleKey: "people",
      moduleLabel: "People",
      label:
        `${c.first_name || ""} ${c.last_name || ""}`.trim() ||
        c.reminder_mail ||
        `Person #${c.id}`,
    })),
  ...companies
    .filter((c) => c?.id)
    .map((c) => ({
      id: c.id,
      entityType: "contact",
      moduleKey: "company",
      moduleLabel: "Company",
      label: c.company_name || `Company #${c.id}`,
    })),
  ...activities
    .filter((a) => a?.id)
    .map((a) => ({
      id: a.id,
      entityType: "activity",
      moduleKey: "activity",
      moduleLabel: "Activity",
      label: a.description || a.category?.name || `Activity #${a.id}`,
    })),
  ...bills
    .filter((b) => b?.id)
    .map((b) => ({
      id: b.id,
      entityType: "bill",
      moduleKey: "bill",
      moduleLabel: "Bill",
      label: b.invoice_num ? `Invoice #${b.invoice_num}` : `Bill #${b.id}`,
    })),
  ...bankAccounts
    .filter((a) => a?.id)
    .map((a) => ({
      id: a.id,
      entityType: "bank_account",
      moduleKey: "bank_account",
      moduleLabel: "Bankers",
      label: a.account_name || a.institution || `Bank account #${a.id}`,
    })),
  ...bankTransactions
    .filter((t) => t?.id)
    .map((t) => ({
      id: t.id,
      entityType: "bank_transaction",
      moduleKey: "bank_transaction",
      moduleLabel: "Bank transaction",
      label: t.description || t.payer_payee || `Transaction #${t.id}`,
    })),
  ...documents
    .filter((d) => d?.id)
    .map((d) => ({
      id: d.id,
      entityType: "document",
      moduleKey: "document",
      moduleLabel: "Document",
      label: d.file_name || `Document #${d.id}`,
    })),
];

export default CommentFeedPanel;
